import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import Card from './ui/Card';
import StatCard from './ui/StatCard';

export interface InventoryStats {
  totalBottles: number;
  totalValue: number;
  byWineType: Record<string, number>;
  lowStockCount: number;
}

export interface InventoryStatsCardsProps {
  stats: InventoryStats | null;
  loading?: boolean;
  onLowStockClick?: () => void;
}

const defaultStats: InventoryStats = {
  totalBottles: 0,
  totalValue: 0,
  byWineType: {},
  lowStockCount: 0
};

const InventoryStatsCards: React.FC<InventoryStatsCardsProps> = ({
  stats,
  loading = false,
  onLowStockClick
}) => {
  const [wineTypesExpanded, setWineTypesExpanded] = useState(false);
  const data = stats ?? defaultStats;

  const wineTypeEntries = Object.entries(data.byWineType);
  const wineTypesSummary =
    wineTypeEntries.length === 0
      ? 'None'
      : wineTypeEntries.length <= 3
        ? wineTypeEntries.map(([k, v]) => `${k}: ${v}`).join(', ')
        : `${wineTypeEntries.length} types`;

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white p-6 rounded-xl border border-monday-border-light shadow animate-pulse"
          >
            <div className="h-4 bg-monday-header rounded w-24 mb-3" />
            <div className="h-8 bg-monday-header rounded w-16" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title="Total Bottles"
        value={data.totalBottles.toLocaleString()}
        icon={FiIcons.FiArchive({ size: 20 })}
        color="primary"
      />
      <StatCard
        title="Total Value"
        value={
          typeof data.totalValue === 'number'
            ? `$${data.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '$0.00'
        }
        icon={FiIcons.FiDollarSign({ size: 20 })}
        color="success"
      />
      <Card className="hover:shadow-lg">
        <div className="flex items-center gap-3 mb-3">
          <div className="inline-flex p-2.5 bg-info-50 rounded-xl text-info-600 ring-1 ring-inset ring-current/10">
            {FiIcons.FiGrid({ size: 20 })}
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-monday-text-muted">
            Wine Types
          </p>
        </div>
        <p className="text-3xl font-extrabold tracking-tight text-monday-text mb-1">
          {wineTypeEntries.length}
        </p>
        <div className="text-xs text-monday-text-muted">
          {!wineTypesExpanded ? (
            <button
              type="button"
              onClick={() => setWineTypesExpanded(true)}
              className="text-primary-600 hover:underline"
            >
              {wineTypesSummary}
            </button>
          ) : (
            <ul className="space-y-0.5">
              {wineTypeEntries.map(([type, count]) => (
                <li key={type}>
                  {type}: {count}
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={() => setWineTypesExpanded(false)}
                  className="text-primary-600 hover:underline"
                >
                  Collapse
                </button>
              </li>
            </ul>
          )}
        </div>
      </Card>
      <div
        className={onLowStockClick ? 'cursor-pointer' : ''}
        onClick={onLowStockClick}
        onKeyDown={(e) => onLowStockClick && e.key === 'Enter' && onLowStockClick()}
        role={onLowStockClick ? 'button' : undefined}
        tabIndex={onLowStockClick ? 0 : undefined}
      >
        <StatCard
          title="Low Stock Alerts"
          value={data.lowStockCount}
          subtitle={onLowStockClick ? 'Click to filter' : undefined}
          icon={FiIcons.FiAlertTriangle({ size: 20 })}
          color="warning"
        />
      </div>
    </div>
  );
};

export default InventoryStatsCards;
